import { Editor } from '@tinymce/tinymce-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import AIImprovementPanel from '../../../../components/AIImprovement/AIImprovementPanel';
import ColorPickerPanel from '../../../../components/ColorPicker/ColorPickerPanel';
import EmojiPickerPanel from '../../../../components/Emoji/EmojiPickerPanel';
import useAlert from '../../../../hooks/useAlert';
import { RootState } from '../../../../modules';
import { editContent } from '../../../../modules/editor';
import { requestAiRewrite } from '../../../../modules/editor/service';
import { RGBToHex } from '../../../../util/color';
import { copyObject } from '../../../../util/object';
import { stringCss } from '../../../../util/parseStyle';
import { registerFontAwesomeIcons } from '../../../../util/tinymceIcons';
import { hasHttp, isUrl, isValidLink } from '../../../../util/validator';

import iconTextAlignCenter from '../../../../assets/images/icon/toolbar/align-center.svg';
import iconTextAlignJustify from '../../../../assets/images/icon/toolbar/align-justify.svg';
import iconTextAlignLeft from '../../../../assets/images/icon/toolbar/align-left.svg';
import iconTextAlignRight from '../../../../assets/images/icon/toolbar/align-right.svg';
import MergeButton from '../../../../components/MergeButton';
import useDebounce from '../../../../hooks/useDebounce';
import { removeInvalidStyleAndAttr } from '../../../../util/dom';
import { addBrLine, cleanUpHtml } from '../../../../util/html';
import { isAllowedFieldForMailMerge } from '../../../../util/mailMerge';
import { addLinkToText, trimBlankString } from '../../../../util/string';
import { useHistoryMode } from '../../../History/HistoryContext';
import TextEditCover from '../TextEditCover';
import { toolbar } from './meta';
import useToolbarFloat from './useToolbarFloat';

const parser = new DOMParser();

type TextEditProps = {
	innerContent: any;
	contentField: string;
	style: any;
	boxStyle: any;
	contentId: number;
	contentIndex: number;
	subIndex?: number;
	markToolbar: (contentId: number, currentIndex: number, itemType: string) => void;
	markedToolbar: any;
	contentCategory?: string;
	textTemplate?: boolean;
};

function TextEdit({ innerContent, contentField, style, boxStyle, contentId, contentIndex, subIndex, markToolbar, markedToolbar, contentCategory, textTemplate }: TextEditProps) {
	const { isInHistory } = useHistoryMode();
	const dispatch = useDispatch();
	const Alert = useAlert();
	const editorRef = useRef<Editor>(null);
	const isAfterLocalSave = useRef(false);
	const [tinymce, setTinymce] = useState({} as any);
	const [debouncedEditedText, editedText, setEditedText] = useDebounce(addBrLine(innerContent[contentField]), 3000);
	const [showToolbar, setShowToolbar] = useState(false);
	const [showContent, setShowContent] = useState(true);
	const [listInfoFields, setListInfoFields] = useState([] as any);
	const [listVarsFields, setListVarsFields] = useState([] as any);
	const [foreColorPickerShow, setForeColorPickerShow] = useState(false);
	const [foreColorValue, setForeColorValue] = useState('');
	const [backColorPickerShow, setBackColorPickerShow] = useState(false);
	const [backColorValue, setBackColorValue] = useState('');
	const [emojiShow, setEmojiShow] = useState(false);
	const [colorPanelFixed, setColorPanelFixed] = useState(false);
	const [colorPanelFixedLeft, setColorPanelFixedLeft] = useState(0);
	const [aiModalShow, setAiModalShow] = useState(false);
	const [selectedTextForAi, setSelectedTextForAi] = useState('');
	const [improvedText, setImprovedText] = useState('');
	const [isAiLoading, setIsAiLoading] = useState(false);
	const [aiRequestCount, setAiRequestCount] = useState(1);
	const [aiMaxRequestCount, setAiMaxRequestCount] = useState(10);
	const [aiError, setAiError] = useState('');
	const textAlignChanged = useRef<boolean>();

	const email = useSelector((state: RootState) => state.editor.email);
	const { listInfo, selectedContentId, prevSelectedContentId, container, listVars, systemFields, contents } = useSelector((state: RootState) => state.editor);
	const { commonStyle } = container;
	const accountPlan = useSelector((state: RootState) => state.auth.account.billing.plan);
	const service = useSelector((state: RootState) => state.auth.service);
	const isNoLinkValidationAccount = useSelector((state: RootState) => state.auth.account.options.editorNoLinkValidation);
	const msg = useSelector((state: RootState) => state.editor.service.msg);

	const contentTarget = subIndex !== undefined ? `${contentId}/content/${contentIndex}/${subIndex}` : `${contentId}/content/${contentIndex}`;
	const styleTarget = `${contentId}/style/${contentIndex}`;

	const whitelist = useMemo(() => {
		const fields = systemFields?.systemMergeURLFields || [];
		return fields?.map(({ substitution }) => `$%${substitution}%$`);
	}, [systemFields]);

  useToolbarFloat(editorRef, {
    onTopIntersectOut: (left) => {
      setColorPanelFixed(false);
      setColorPanelFixedLeft(left);
    },
		onTopIntersectIn: (left) => {
      setColorPanelFixed(true);
      setColorPanelFixedLeft(left);
    },
		onBottomIntersectIn: (left) => {
      setColorPanelFixed(true);
      setColorPanelFixedLeft(left);
    },
		onBottomIntersectOut: () => {
			setColorPanelFixed(false);
		},
  })

	useEffect(() => {
		const initialText = addBrLine(innerContent[contentField]);
		if (initialText === debouncedEditedText) return;
		dispatch(editContent({ target: `${contentTarget}/text`, data: debouncedEditedText, mode: 'force' }));
		isAfterLocalSave.current = true;
	}, [debouncedEditedText]);

	useEffect(() => {			
		if (isInHistory) return;
		if (selectedContentId !== contentId) {
			isAfterLocalSave.current = false;
			init();
		}		
		if (prevSelectedContentId === contentId) {
			
			if (editedText.length === 0) {
				return;
			}
			dispatch(editContent({ target: `${contentTarget}/text`, data: editedText }));
			removeTempTextFromLocal();	
		}
	}, [selectedContentId]);

	useEffect(() => {
		if (isInHistory) return;
		if (editedText.length !== 0 && !contentCategory?.includes('shop')) {
			dispatch(editContent({ target: `${contentTarget}/text`, data: editedText }));
			removeTempTextFromLocal();
		}
		
		const toolbarIndex = subIndex !== undefined ? Number(`${contentIndex}${subIndex}`) : contentIndex;
		if (markedToolbar?.contentId !== contentId || markedToolbar?.currentIndex !== toolbarIndex || markedToolbar.itemType !== 'text') {
			isAfterLocalSave.current = false;
			init();
		} 
	}, [markedToolbar]);

	useEffect(() => {
		setListInfoFields([
			{ label: '회사/단체명', value: listInfo.companyName },
			{ label: '주소', value: listInfo.companyAddress },
			{ label: '연락처', value: listInfo.companyContact },
		]);
	}, [listInfo]);

	useEffect(() => {
		const filtedListVars = listVars.filter(isAllowedFieldForMailMerge);
		const filterdSystemFields = systemFields.systemMergeSubscriberFields
			.filter(field => field.substitution === "subscribed_date")
			.map(field => ({ ...field, label: "구독일" }));
		const updatedListVars = [
			...filtedListVars,
			...filterdSystemFields
		];
		
		setListVarsFields(updatedListVars);
	}, [listVars, systemFields]);

	useEffect(() => {
		
		setEditedText(addBrLine(innerContent[contentField]));
		init();
	}, [innerContent]);

	const init = () => {
		if (isAfterLocalSave.current) return;

		toggleForeColorPickerShow(false);
		toggleBackColorPickerShow(false);
		toggleEmojiPicker(false);
		toggleAiModal(false);

		if (!textTemplate) {
			setShowContent(true);
			setShowToolbar(false);
		}

		isAfterLocalSave.current = false;
	}

	useEffect(() => {
		if (textTemplate) {
			setShowToolbar(true);
		}
	}, [textTemplate])


	const startEditing = () => {
		const toolbarIndex = subIndex !== undefined ? Number(`${contentIndex}${subIndex}`) : contentIndex;
		markToolbar(contentId, toolbarIndex, 'text');

		setTimeout(() => {
			setShowToolbar(true);
		}, 10);

		setTimeout(() => {
			checkEditorToolbar();
		}, 500);
	}

	const checkEditorToolbar = () => {
		let fontFamilyButton = document?.querySelector('.tox-tbtn[title="서체"]');
		let fontSizeButton = document?.querySelector('.tox-tbtn[title="폰트 사이즈"]');
		fontFamilyButton?.setAttribute('style', `width: 85px`);
		fontSizeButton?.setAttribute('style', `width: 70px`);
	}

	const handleEditorChange = (innerHTML: string) => {
		setEditedText(addBrLine(tinymce.getContent()));
	}

	const detectSelection = (editor:any) => {
		toggleForeColorPickerShow(false);
		toggleBackColorPickerShow(false);

		if (editor.selection === null) {
			return;
		}

		const node = editor.selection.getNode();

		// color
		let color: string = editor.selection.dom.getStyle(node, 'color') || '';
		let bg: string = editor.selection.dom.getStyle(node, 'background-color') || '';

		if (color.length === 0) {
			color = RGBToHex(window?.getComputedStyle(node).getPropertyValue('color'));
		}
		if (bg.length === 0) {
			bg = RGBToHex(window?.getComputedStyle(node).getPropertyValue('background-color'));
		}

		let bgTarget: any = document?.querySelector('.tox-tbtn--select.back-color .tox-icon');
		let bgCurrentColor: any = document?.querySelector('.tox .tox-tbtn--select.back-color .back-current-color');

		if (bgCurrentColor === null) {
			let bgBar = document.createElement('div');
			bgBar.classList.add('back-current-color');
			bgBar.style.backgroundColor = bg;
			bgTarget.appendChild(bgBar);
		} else {
			bgCurrentColor.style.backgroundColor = bg;
		}

		let textTarget: any = document?.querySelector('.tox-tbtn--select.fore-color');
		let textCurrentColor: any = document?.querySelector('.tox .tox-tbtn--select.fore-color .text-current-color');

		if (textCurrentColor === null) {
			let colorBar = document.createElement('div');
			colorBar.classList.add('text-current-color');
			colorBar.style.backgroundColor = color;
			textTarget.appendChild(colorBar);
		} else {
			textCurrentColor.style.backgroundColor = color;
		}

		if (color.length !== 0) {
			setForeColorValue(color);
		}
		if (bg.length !== 0) {
			if (bg === 'transparent') {
				bg = '';
			}
			setBackColorValue(bg);
		}

		// align 
		let textAlignIconParent = document?.querySelector('.tox-tbtn.tox-tbtn--select.align > .tox-icon');
		let svgs = document?.querySelector('.tox-tbtn.tox-tbtn--select.align > .tox-icon svg');
		let imgs = document?.querySelector('.tox-tbtn.tox-tbtn--select.align > .tox-icon img');
		svgs?.remove();
		imgs?.remove();

		let n = document?.createElement('img');
		switch (getCurrentAlign(editor)) {
			case '가운데':
				n.setAttribute('src', iconTextAlignCenter);
				textAlignIconParent?.setAttribute('title', '가운데');
				break;
			case '왼쪽':
				n.setAttribute('src', iconTextAlignLeft);
				textAlignIconParent?.setAttribute('title', '왼쪽');
				break;
			case '오른쪽':
				n.setAttribute('src', iconTextAlignRight);
				textAlignIconParent?.setAttribute('title', '오른쪽');
				break;
			case '양쪽정렬':
				n.setAttribute('src', iconTextAlignJustify);
				textAlignIconParent?.setAttribute('title', '양쪽정렬');
				break;
			default:
				n.setAttribute('src', iconTextAlignLeft);
				break;
		}

		textAlignIconParent?.appendChild(n);
	}

	const customSetup = (editor: any) => {
		setTinymce(editor);
		
		// FontAwesome 아이콘들을 TinyMCE에 등록
		registerFontAwesomeIcons();

		editor.on('NodeChange', (e: any) => {
			if (textAlignChanged.current) {
				textAlignChanged.current = false;
				detectSelection(editor);
			}
			/* 
			# 불릿 컬러 보정
			LI 태그가 포함한 SPAN 태그가 1개일 경우에 LI에 유일한 SPAN의 스타일을 적용한다.
			*/
			let dom: any = parser.parseFromString(editor.getContent(), 'text/html');
			
			// Get depth and text of each leaf node
			// function getLeafNodeInfo(node: Node, currentDepth: number = 0): {depth: number, text: string}[] {
			// 	// If node has no children, it's a leaf node - return its depth and text
			// 	if (!node.childNodes.length) {
			// 		return [{
			// 			depth: currentDepth,
			// 			text: node.textContent || ''
			// 		}];
			// 	}
				
			// 	// Recursively get info of leaf nodes in children
			// 	let nodeInfo: {depth: number, text: string}[] = [];
			// 	for (let child of Array.from(node.childNodes)) {
			// 		nodeInfo = nodeInfo.concat(getLeafNodeInfo(child, currentDepth + 1));
			// 	}
			// 	return nodeInfo;
			// }
			
			// const leafNodeInfo = getLeafNodeInfo(dom);
			// console.log("Leaf node info:", leafNodeInfo);
			
			let liTagList = dom.querySelectorAll('li');

			for (let index = 0; index < Array.from(liTagList).length; index++) {
				let element = liTagList[index];
				let children = element.childNodes;
				if (Array.from(children).filter((item: any) => item.nodeName === 'SPAN').length === 1) {
					const { color, fontSize } = element.childNodes[0].style ?? {};
					try {
						//font-size 있을때만 font-size style 추가
						element.setAttribute('style', `${color != null ? `color: ${color}` : ''}; ${fontSize != null ? `font-size: ${fontSize};'` : ''}`);
					} catch (error) {
						console.error(error);
					}

					try {
						if (element?.childNodes[0]?.nodeName === '#text') {
							element.setAttribute('style', '');
						}
					} catch (error) {
						console.error(error);
					}
				}
			}

			saveTempTextToLocal(addBrLine(dom?.documentElement?.querySelector('body')?.innerHTML));

			setEditedText(addBrLine(dom?.documentElement?.querySelector('body')?.innerHTML));
		});

		editor.on('keyup', (e: any) => {
			if (editor.getContent()?.length === 0) {
				editor.setContent('&nbsp;&nbsp;')
			}
		});

		editor.on('ExecCommand', (e: any) => {
			const { command, value } = e;
			if (command === 'FontSize') {
				var node = editor.selection.getNode();

				if (node) {
					let childNodes = node.getElementsByTagName('li');
					if (childNodes) {
						for (let index = 0; index < childNodes.length; index++) {
							const element = childNodes[index];
							element.setAttribute('style', `font-size: ${value}`);
						}
					}
				}
				//toolbar link
			} else if (command === 'mceInsertLink') {
				let node = editor.selection.getNode();

				let childNodes = node.getElementsByTagName('a');
				if (childNodes) {
					for (let index = 0; index < childNodes.length; index++) {
						const element = childNodes[index];
						element.setAttribute('style', stringCss(commonStyle.link, true));
					}
				}
				//auto link
			} else if (command === 'createlink') {
				let node = editor.selection.getNode();
				let isMailtoLink = node.getAttribute('href')?.indexOf('mailto:') > -1

				if (isMailtoLink) {
					node.setAttribute('class', 'stb-mailto')
					node.setAttribute('style', stringCss(commonStyle.text, true))
				} else {
					node.setAttribute('style', stringCss(commonStyle.link, true))
				}
			}

		});

		// 커서 위치 변경
		setTimeout(() => {
			if (editor === null) {
				return;
			}
			editor.selection.select(editor.getBody(), true);
			editor.selection.collapse(false);
		}, 300);

		// 에디터 활성 직후 detect 
		setTimeout(() => {
			detectSelection(editor);
		}, 300);

		editor.on('click', (e: any) => {
			detectSelection(editor);
		})

		editor.on('keyup', (e: any) => {
			detectSelection(editor);
		})

		editor.on('submit', (e: any) => {
			e.preventDefault();
			return false;
		});

		editor.on('OpenWindow', (e: any) => {
			window.parent.postMessage({ msg: 'dialogShow' }, '*');

			// 링크 윈도우 예외처리
			const validateLinkInput = (v: string) => {
				let value = trimBlankString(v);

				if (value.length === 0) {
					return { isValid: false, value: null, message: Alert?.msg?.REQUIRE_URL };
				}

				if (isNoLinkValidationAccount) {
					return { isValid: true, value };
				}

				if (!isValidLink(value, whitelist)) {
					return { isValid: false, value: null, message: Alert?.msg?.CHECK_URL_TYPE };
				}

				if (isUrl(value) && !hasHttp(value)) {
					value = `http://${value}`;
				}
				return { isValid: true, value };
			}

			const submit = (e: Event) => {
				clearError();
				const a: any = document.querySelector('.tox-control-wrap input');
				const { isValid, value, message } = validateLinkInput(a.value);

				if (!isValid) {
					e.stopPropagation();
					Alert.error(Alert?.msg?.DEFAULT_ERROR);
					setError(message || Alert?.msg?.DEFAULT_ERROR);
					return;
				}

				a.value = value;
			}

			const setError = (text: string) => {
				const msg: HTMLDivElement | null = document.querySelector('div.stb-link-error');
				if (!msg) return;
				msg.innerText = text;
				msg.classList.add('show');
			}

			const clearError = () => {
				const msg: HTMLDivElement | null = document.querySelector('div.stb-link-error');
				if (!msg) return;
				msg.innerText = '';
				msg.classList.remove('show');
			}

			const linkTagElement: HTMLDivElement = document.createElement('div');

			const showLinkTag = (e: any) => {
				linkTagElement.classList.add('link-form-dropdown');
				linkTagElement.innerHTML = `
				<button data-value="$%unsubscribe%$"><strong>수신거부</strong><em>$%unsubscribe%$</em></button><button data-value="$%permalink%$"><strong>웹에서 보기</strong><em>$%permalink%$</em></button>
				`;
				const btns = Array.from(linkTagElement.querySelectorAll('button'));
				const inputTarget = e.target;
				for (const btn of btns) {
					btn.addEventListener('click', (e: any) => {
						inputTarget.value = btn.dataset.value
						removeLinkTag(linkTagElement);
					})
				}
				e.target.parentNode.insertBefore(linkTagElement, e.target.nextSibling);
			}

			const removeLinkTag = (element: any) => {
				element.remove();
			}

			if (e.dialog.getData().hasOwnProperty('url')) {
				const dialog = document.querySelector('.tox-dialog');
				dialog?.classList?.add('link-dialog');

				const headerTitle: HTMLDivElement | null = document.querySelector('.tox-dialog .tox-dialog__header .tox-dialog__title');
				if (headerTitle) {
					headerTitle.innerHTML = 'URL을 입력하세요';
				}

				const wrapper = document.querySelector('.tox-control-wrap');
				if (wrapper) {
					const msg = document.createElement('div');
					msg.classList.add('stb-link-error');
					wrapper.parentElement?.appendChild(msg);

					const containter = document.createElement('div');
					wrapper.appendChild(containter);

					const onSelect = (value: string) => {
						const input = wrapper.querySelector('input');
						if (!input) return;
						input.value += value;
					}

					const onClose = () => (dialog as HTMLElement)?.focus();

					ReactDOM.render(<MergeButton mode="inputright" accountPlan={accountPlan} listVars={listVars} systemFields={systemFields?.systemMergeURLFields || []} onSelect={onSelect} onClose={onClose}></MergeButton>, containter);
				}
				document.querySelector('.tox-dialog__footer-end > button:nth-child(2)')?.addEventListener('click', (e: any) => {
					submit(e);
				});
				document.querySelector('.tox-control-wrap input')?.addEventListener('keydown', (e: any) => {
					if (e.key === 'Enter') {
						submit(e);
					}
				});
				document.querySelector('.tox-control-wrap input')?.addEventListener('keyup', (e: any) => {
					if (e.target.value === '$') {
						if (document.querySelector('.link-form-dropdown') !== null) {
							return;
						}
						showLinkTag(e);
					} else {
						removeLinkTag(linkTagElement);
					}
				});
				document.querySelector('.tox-dialog-wrap')?.addEventListener('click', (e: any) => {
					removeLinkTag(linkTagElement);
				})
			}
		});

		editor.on('CloseWindow', (e: any) => {
			window.parent.postMessage({ msg: 'dialogHide' }, '*');
		});

		editor.ui.registry.addButton('stbUnlink', {
			icon: 'unlink',
			onAction: function () {
				Alert.confirm(Alert?.msg?.UNLINK_TEXT, () => {
					editor.execCommand('Unlink');
					setTimeout(() => {
						const node = editor.selection.getNode();
						if (RGBToHex(node.style.color) === RGBToHex(commonStyle.link.color)) {
							editor.execCommand('removeformat');
						}
						setEditedText(addBrLine(editor.getContent()));
					}, 10);
				})
			}
		});

		editor.ui.registry.addButton('stbForeColorPicker', {
			icon: 'text-color',
			text: 'stb-fore-color-picker',
			onSetup: function (buttonApi: any) {
				let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-fore-color-picker');

				a?.classList.add('blind');
				a?.parentElement?.classList.add('fore-color');
			},
			onAction: function () {
				toggleForeColorPickerShow(true);
			}
		});

		editor.ui.registry.addButton('stbBackColorPicker', {
			icon: 'highlight-bg-color',
			text: 'stb-back-color-picker',
			onSetup: function (buttonApi: any) {
				let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-back-color-picker');
				a?.classList.add('blind');
				a?.parentElement?.classList.add('back-color');
			},
			onAction: function () {
				toggleBackColorPickerShow(true);
			}
		});

		editor.ui.registry.addButton('stbEmoji', {
			icon: 'emoji',
			text: 'stb-emoji-picker',
			onSetup: function (buttonApi: any) {
				let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-emoji-picker');
				a?.classList.add('blind');
				a?.parentElement?.classList.add('emoji');
			},
			onAction: function () {
				toggleEmojiPicker(true);
			}
		});

		editor.ui.registry.addMenuButton('mergeTagButton', {
			icon: 'merge',
			text: 'stb-merge',
			onSetup: function (buttonApi) {
				let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-merge');
				a?.classList.add('blind');
				a?.parentElement?.classList.add('merge');

				a?.parentElement?.addEventListener('click', (e: any) => {
					if (!service.isStandard) {
						const target = e.currentTarget;
						setTimeout(() => {
							if (!target.classList.contains('tox-tbtn--active')) return;
							target.click(); // 버튼 비활성 시키기 위해 한 번 더 클릭
						}, 500)
						return;
					}

					setTimeout(() => {
						let mergeTagLabels: any = document.querySelectorAll('.tox-menu .tox-collection__item-label');
						let mergeTagLabelsList: any = Array.from(mergeTagLabels);
						mergeTagLabelsList.forEach((item: any) => {
							item.innerHTML = item.innerText.split('|').map((value, i) => i === 1 ? `<small>${value}</small>` : value).join('');
						});
					}, 300);
					setTimeout(() => {
						let mergeTagLabels: any = document.querySelectorAll('.tox-menu .tox-collection__item');
						let mergeTagLabelsList: any = Array.from(mergeTagLabels);
						mergeTagLabelsList.forEach((item: any) => {
							item.classList.add('ready');
						});
					}, 350);
				});
			},
			fetch: function (callback: any) {
				const a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-merge');
				const isClicked = a?.parentElement?.classList.contains('tox-tbtn--active');
				if (!service.isStandard) {
					if (!isClicked) return;
					window.parent.postMessage({ msg: 'openUpgradeGuideModal', guideType: 'mailMerge' }, '*');
					return;
				}

        const hasNoList = email.listId === -1 || email.listId === undefined || email.isListDeleted;
				if (hasNoList) {
					Alert.error(msg.CHECK_SELECT_LISTS);
					return;
				}

				const listInfoFieldsItems = listInfoFields.map((item: any) => {
					return {
						type: 'menuitem',
						text: `${item.label}`,
						onAction: function () {
							editor.insertContent(`${item.value}`);
						}
					}
				});

				const listVarsItems = listVarsFields.map((item: any) => {
					return {
						type: 'menuitem',
						text: `${item.label}|${item.substitution}`,
						// text: `${item.label} <small>${item.substitution}</small>`,
						onAction: function () {
							editor.insertContent(`$%${item.substitution}%$`);
						}
					}
				});

				const listInfoTitle = [
					{
						type: 'menuitem',
						text: '주소록 필드'
					}
				];

				const listVarsTitle = [
					{
						type: 'menuitem',
						text: '사용자 정의 필드'
					}
				];

				const preventUseMergeItem = [
					{
						type: 'menuitem',
						text: '사용자 정의 필드를 사용하려면<br /><em>업그레이드</em> 하세요',
						onAction: function () {
							window.parent.postMessage({ msg: 'openAccountPlanModal' }, '*');
						}
					}
				]

				const items = accountPlan >= 2 ? [...listInfoTitle, ...listInfoFieldsItems, ...listVarsTitle, ...listVarsItems] : preventUseMergeItem;
				setTimeout(() => {
					callback(items);
				}, 200);
			}
		});

		editor.ui.registry.addMenuButton('stbAlign', {
			icon: 'align-left',
			text: 'stb-align',
			onSetup: function () {
				let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-align');
				a?.classList.add('blind');
				a?.parentElement?.classList.add('align');
			},
			onselect: function (e: any) {
				editor.execCommand(this.value());
			},
			fetch: function (callback: any) {
				setTimeout(() => {
					let menuPanel = document.querySelector('.tox-selected-menu');
					menuPanel?.classList.add('stb-align-menu');
					const currentAlign = getCurrentAlign(editor);
					let cm = currentAlign?.includes('양쪽') ? document.querySelector(`.tox-selected-menu .tox-collection__item[title="${currentAlign}"]`) : document.querySelector(`.tox-selected-menu .tox-collection__item[title="${currentAlign?.replace('정렬', '')}"]`);
					cm?.classList.add('tox-collection__item--enabled');
				}, 10);

				callback([
					{
						type: 'menuitem',
						text: 'left',
						icon: 'align-left',
						onAction: function () {
							textAlignChanged.current = true;
							editor.execCommand('JustifyLeft');
							detectSelection(editor);
						}
					},
					{
						type: 'menuitem',
						text: 'center',
						icon: 'align-center',
						onAction: function () {
							textAlignChanged.current = true;
							editor.execCommand('JustifyCenter');
							detectSelection(editor);
						}
					},
					{
						type: 'menuitem',
						text: 'right',
						icon: 'align-right',
						onAction: function () {
							textAlignChanged.current = true;
							editor.execCommand('JustifyRight');
							detectSelection(editor);
						}
					},
					{
						type: 'menuitem',
						text: 'justify',
						icon: 'align-justify',
						onAction: function () {
							textAlignChanged.current = true;
							editor.execCommand('JustifyFull');
							detectSelection(editor);
						}
					},
				]);
			},
		});

		// AI 글쓰기 개선 버튼 추가
		editor.ui.registry.addButton('stbAiImprove', {
			icon: 'sparkles',
			tooltip: 'AI로 다듬기',
			onSetup: function (buttonApi: any) {
				// 간단하게 클래스만 추가
				setTimeout(() => {
					let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === '✨');
					if (a?.parentElement) {
						a?.parentElement?.classList.add('ai-improve');
					}
				}, 100);
			},
			onAction: async function () {
				const selectedText = editor.selection.getContent({ format: 'text' });
				if (selectedText.trim().length === 0) {
					Alert.error('텍스트를 선택해 주세요.');
					return;
				}
				
				// 믹스패널 이벤트: 기능 버튼 클릭
				window.parent.postMessage({ msg: 'textPolishingButtonClicked' }, '*');
				
				setSelectedTextForAi(selectedText);
				setAiModalShow(true);
				setIsAiLoading(true);
				setAiError('');
				
				// 부모 창에 모달 열림 알림
				window.parent.postMessage({ msg: 'dialogShow' }, '*');
				
				// 공통 함수 사용
				await requestAiImprovement(selectedText);
			}
		});
	}

	const saveTempTextToLocal = (text: string) => {
		let key = `stb_temp_textEdit_${contentId}_${contentIndex}`;
		localStorage.setItem(key, text);
	}

	const removeTempTextFromLocal = () => {
		let key = `stb_temp_textEdit_${contentId}_${contentIndex}`;
		localStorage.removeItem(key);
	}

	const extractTextAlign = (styleText: string) => {
		const regex = /text-align:\s*([^;]+);/;
		const match = styleText.match(regex);
		if (match) {
			return match[1].trim();
		}
		return null;
	}

	const getCurrentAlign = (editor: any) => {
    const currentNodeStyle = editor.selection.getNode()?.getAttribute('style');
		const textAlign = extractTextAlign(currentNodeStyle || '') || style?.textAlign;

    if (textAlign === 'left') {
      return '왼쪽';
    } else if (textAlign === 'center') {
			return '가운데';
		} else if (textAlign === 'right') {
			return '오른쪽';
		} else if (textAlign === 'justify') {
			return '양쪽정렬';
		} else {
			return null;
		}
	}

	const onChangeForeColorValue = (value: string) => {
		if (value === '') {
			const node = tinymce.selection.getNode();

			let color: string = tinymce.selection.dom.getStyle(node, 'color') || '';
			let bg: string = tinymce.selection.dom.getStyle(node, 'background-color') || '';

			setForeColorValue('');
			tinymce.execCommand('mceRemoveTextcolor', 'forecolor');
		} else {
			setForeColorValue(value);
			tinymce.execCommand('mceApplyTextcolor', 'forecolor', value);
		}
		// applyBulletStyle('forecolor', value);
	}

	const onChangeBackColorValue = (value: string) => {
		if (value === '') {
			setBackColorValue('');
			tinymce.execCommand('mceRemoveTextcolor', 'hilitecolor');
		} else {
			setBackColorValue(value);
			tinymce.execCommand('mceApplyTextcolor', 'hilitecolor', value);
		}
	}

	// const applyBulletStyle = (mode:string, value:string) => {
	// 	const node = tinymce.selection.getNode();
	// 	if (node?.parentElement?.nodeName === 'LI') {
	// 		let pLi:HTMLLIElement = node.parentElement;
	// 		pLi.style.color = value;
	// 	}
	// }

	const onEmojiClick = (event: any, emojiObject: any) => {
		tinymce.execCommand('mceInsertContent', false, emojiObject.emoji);
		toggleEmojiPicker(false);
	}

	const toggleForeColorPickerShow = (show: boolean) => {
		setForeColorPickerShow(show);
		let btn = Array.from(document.querySelectorAll('span')).find(el => el.textContent === 'stb-fore-color-picker');
		if (show) {
			btn?.parentElement?.classList.add('tox-tbtn--enabled');
		} else {
			btn?.parentElement?.classList.remove('tox-tbtn--enabled');
		}
	}

	const toggleBackColorPickerShow = (show: boolean) => {
		setBackColorPickerShow(show);
		let btn = Array.from(document.querySelectorAll('span')).find(el => el.textContent === 'stb-back-color-picker');
		if (show) {
			btn?.parentElement?.classList.add('tox-tbtn--enabled');
		} else {
			btn?.parentElement?.classList.remove('tox-tbtn--enabled');
		}
	}

	const toggleEmojiPicker = (show: boolean) => {
		setEmojiShow(show);
		let btn = Array.from(document.querySelectorAll('span')).find(el => el.textContent === 'stb-emoji-picker');
		if (show) {
			btn?.parentElement?.classList.add('tox-tbtn--enabled');
		} else {
			btn?.parentElement?.classList.remove('tox-tbtn--enabled');
		}
	}

	const toggleAiModal = (show: boolean) => {
		setAiModalShow(show);
		if (!show) {
			// 모달 닫을 때 모든 상태 초기화
			setSelectedTextForAi('');
			setImprovedText('');
			setIsAiLoading(false);
			setAiError('');

				// AI 모달이 실제로 열려있었을 때만 부모 창에 알림
			if (aiModalShow) {
				window.parent.postMessage({ msg: 'dialogHide' }, '*');
			}
		}
		let btn = Array.from(document.querySelectorAll('span')).find(el => el.textContent === 'AI 글쓰기 개선');
		if (show) {
			btn?.parentElement?.classList.add('tox-tbtn--enabled');
		} else {
			btn?.parentElement?.classList.remove('tox-tbtn--enabled');
		}
	}

	const handleAppendBelow = () => {
		// 믹스패널 이벤트: 뒤에 추가하기
		window.parent.postMessage({ msg: 'textPolishingAppended' }, '*');
		
		let startMarkerId: string;
		let endMarkerId: string;
		
		// 실행취소를 위한 트랜잭션으로 감싸기
		tinymce.undoManager.transact(() => {
			// 선택 영역의 끝 위치로 커서를 이동
			tinymce.selection.collapse(false);
			
			// 마커를 추가해서 추가된 텍스트 부분을 식별
			const timestamp = Date.now();
			startMarkerId = `start_marker_${timestamp}`;
			endMarkerId = `end_marker_${timestamp}`;
			
			// insertContent를 사용하여 인라인으로 텍스트 삽입
			const escapedImprovedText = tinymce.dom.encode(improvedText);
			tinymce.insertContent(`<span id="${startMarkerId}"></span>${escapedImprovedText}<span id="${endMarkerId}"></span>`);
		});
		
		// 에디터에 포커스를 주고 추가된 텍스트 부분을 선택
		setTimeout(() => {
			tinymce.focus();
			const startMarker = tinymce.dom.get(startMarkerId);
			const endMarker = tinymce.dom.get(endMarkerId);
			
			// 마커가 있으면 그 사이의 텍스트를 선택
			if (startMarker && endMarker) {
				const range = tinymce.dom.createRng();
				range.setStartAfter(startMarker);
				range.setEndBefore(endMarker);
				tinymce.selection.setRng(range);
				
				// 마커 제거
				tinymce.dom.remove(startMarker);
				tinymce.dom.remove(endMarker);
			}
		}, 10);
		
		toggleAiModal(false);
	}

	const handleReplace = () => {
		// 믹스패널 이벤트: 바로 적용하기
		window.parent.postMessage({ msg: 'textPolishingApplied' }, '*');
		
		// 실행취소를 위한 북마크 생성
		tinymce.undoManager.transact(() => {
			// 선택된 텍스트를 개선된 텍스트로 대체
			tinymce.selection.setContent(improvedText);
		});
		
		// 에디터에 포커스를 주고 커서를 삽입된 텍스트 뒤로 이동
		setTimeout(() => {
			tinymce.focus();                        // 에디터에 포커스
			const range = tinymce.selection.getRng(); // 현재 범위 가져오기
			range.collapse(false);                  // 범위를 끝으로 축소
			tinymce.selection.setRng(range);        // 새 범위 설정
		}, 10);
		
		toggleAiModal(false);
	}

	const handleCancel = () => {
		// 원본 텍스트 유지, 모달만 닫기
		toggleAiModal(false);
	}

	// 전체 이메일 콘텐츠 추출 함수 (수정 필요)
	const extractFullEmailContent = (): string => {
    if (!contents || !Array.isArray(contents)) {
        return '';
    }
    
    return contents
        .filter(item => {
            // AI 문장 개선에 불필요한 category들 제외
            const excludeCategories = ['footer', 'permalink', 'share', 'sns', 'video', 'map', 'partition', 'spacer'];
            return !excludeCategories.includes(item.content?.category);
        })
        .map(item => {
            
            // 🎯 현재 편집 중인 콘텐츠인지 확인하고 최신 텍스트 사용
            if (item.id === contentId) {
                // 현재 편집 중인 텍스트 사용 (실시간 업데이트)
                if (editedText && editedText.trim().length > 0) {
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = editedText;
                    return tempDiv.textContent || tempDiv.innerText || '';
                }
            }
            
            // content.content[0].text에서 텍스트 콘텐츠 추출
            if (item.content?.content && Array.isArray(item.content.content) && item.content.content[0]?.text) {
                // HTML 태그 제거하고 순수 텍스트만 반환
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = item.content.content[0].text;
                return tempDiv.textContent || tempDiv.innerText || '';
            }
            return '';
        })
        .filter(text => text.trim().length > 0) // 빈 텍스트 제거
        .join('\n\n'); // 문단 구분자로 연결
};

	// AI 개선 요청 공통 함수
	const requestAiImprovement = async (text: string, actionName: string = '개선') => {
		setIsAiLoading(true);
		setAiError('');
		
		try {
			// 전체 콘텍스트 추출
			const fullContent = extractFullEmailContent();
			
			// 실제 API 요청 (새로운 형식)
			const result = await requestAiRewrite(email.id, text, fullContent);
			
			setImprovedText(result?.content || '');
			setAiRequestCount(result?.requestCount || 1);
			setAiMaxRequestCount(result?.maxRequestCount || 10);
		} catch (error: any) {
			
			// API 에러 응답 처리
			if (error?.response?.data) {
				const errorData = error.response.data;
				
				// 모든 에러에서 Values 정보가 있으면 카운터 업데이트
				if (errorData.Values) {
					if (typeof errorData.Values.requestCount === 'number') {
						setAiRequestCount(errorData.Values.requestCount);
					}
					if (typeof errorData.Values.maxRequestCount === 'number') {
						setAiMaxRequestCount(errorData.Values.maxRequestCount);
					}
				}
				
				if (errorData.Code === 'Errors.Data.LimitExceeded') {
					// 요청 제한 초과
					setAiError('오늘은 더 이상 사용할 수 없습니다. <br />워크스페이스 기준 하루에 10번 사용할 수 있습니다. <br />내일 다시 시도해 보세요.');
				} else if (errorData.Code === 'Errors.Data.TooShortData') {
					// 텍스트가 너무 짧은 경우
					setAiError('선택한 텍스트가 짧아 다듬을 수 없습니다. <br/> 텍스트를 조금 더 선택해 주세요.');
				} else if (errorData.Code === 'Errors.Data.TooLongData') {
					// 텍스트가 너무 긴 경우
					const currentLength = text.length;
					setAiError(`선택한 텍스트가 길어 다듬을 수 없습니다. <br />3,000자 이하로 줄여 다시 시도해 주세요.`);
				} else {
					// 기타 서버 에러
					setAiError(`이런, 뭔가 잘못된 것 같습니다.<br/>잠시 후 다시 시도해 보세요.`);
				}
			} else {
				// 네트워크 에러 등
				setAiError('네트워크 오류가 발생했습니다.<br/>잠시 후 다시 시도해 보세요.');
			}
		} finally {
			setIsAiLoading(false);
		}
	}

	const handleRetry = async () => {
		// 믹스패널 이벤트: 다시 다듬기
		window.parent.postMessage({ msg: 'textPolishingRefreshClicked' }, '*');
		
		// 다시 다듬기 - 동일한 텍스트로 AI 요청 재시도
		await requestAiImprovement(selectedTextForAi, '다시 다듬기');
	}

	const fontSizeOption = () => {
		const smallFontComponents = ['permalink', 'footer'];
		if (contentCategory === undefined || smallFontComponents.includes(contentCategory)) {
			return '12px 14px 16px 18px 20px 26px';
		} else {
			return '12px 14px 16px 18px 20px 26px 32px 40px 48px 60px';
		}
	}

	const contentStyleParse = () => {
		let s = copyObject(style);
		s['paddingLeft'] = '0!important';
		s['paddingRight'] = '0!important';
		// s['lineHeight'] = boxStyle?.lineHeight; //tinyEditor 열리는 순간의 높이 결정
		s['backgroundColor'] = boxStyle?.background;

		if (textTemplate) {
			s['margin'] = '32px 60px;'
			s['lineHeight'] = '1.5';
			s['fontSize'] = '12px';
		}

		return `body${stringCss(s)} a${stringCss(commonStyle.link)} span>a {font-weight:inherit;font-style:inherit;text-decoration:inherit;}`;
	}

	const handleBlur = () => {
		if (textTemplate) {
			dispatch(editContent({ target: `${contentTarget}/text`, data: editedText, mode: 'force' }));
			removeTempTextFromLocal();
		}
	};


	return (
		<div className={textTemplate ? 'text-edit' : showContent ? 'text-edit notshow' : 'text-edit'} >
			{(showToolbar) && (
				<div className={textTemplate ? "text-template" : ""}>
					<Editor
						apiKey="lkuil8ct6v9pz6arod8pmx2gix11j03f19lbz0lprnbpjn0x"
						ref={editorRef}
						onBlur={handleBlur}
						id={`texteditor${contentId}${contentIndex}`}
						initialValue={editedText}
						onEditorChange={handleEditorChange}
						init={{
							setup: function (editor: any) {
								customSetup(editor);
							},
							init_instance_callback: () => {
								setShowContent(false);
							},
							auto_focus: `texteditor${contentId}${contentIndex}`,
							skin_url: '/assets/tinymce_skin/skins/ui/stb',
							icons_url: '/assets/tinymce_icon/icons/stb/icons.js', // https://www.tiny.cloud/docs/advanced/editor-icon-identifiers/
							icons: 'stb',
							content_css: '/assets/tinymce_skin/skins/content/stb/content.css',
							content_style: `${contentStyleParse()}`,
							autoresize_bottom_margin: 0,
							autoresize_on_init: false,
							paste_data_images: false,
							menubar: false,
							branding: false,
							language: 'ko_KR',
							language_url: '/assets/i18n/tinyMCE_ko_KR.js',
							height: 0,
							plugins: [
								'advlist lists link anchor emoticons paste image autoresize autolink',
							],
							toolbar: textTemplate ? toolbar : [
								`${service.isStarter ? '' : 'stbAiImprove | '}numlist bullist mergeTagButton stbEmoji | removeformat undo redo`,
								'fontselect | fontsizeselect | stbAlign | bold italic underline strikethrough | stbForeColorPicker stbBackColorPicker | link stbUnlink'
							],
							// lineheight_formats: '1 1.5 1.8 2',
							fontsize_formats: `${fontSizeOption()}`,
							font_formats: '고딕=AppleSDGothic, apple sd gothic neo, noto sans korean, noto sans korean regular, noto sans cjk kr, noto sans cjk, nanum gothic, malgun gothic, dotum, arial, helvetica, sans-serif;명조=nanum myoungjo, 바탕, batang, serif',
							// font_formats: '고딕=AppleSDGothic, noto sans, malgun gothic, dotum, sans-serif;명조=nanum myoungjo, batang, serif',
							forced_root_block: 'div',
							force_p_newlines: false,
							force_br_newlines: true,
							convert_newlines_to_brs: true,
							// remove_linebreaks : true,
							link_context_toolbar: false,
							link_title: false,
							link_assume_external_targets: false,
							target_list: false,
							formats: {
								bold: { inline: 'span', classes: 'stb-bold', styles: { fontWeight: 'bold' }, links: true },
								italic: { inline: 'span', classes: 'stb-italic', styles: { fontStyle: 'italic' }, links: true },
								underline: { inline: 'span', classes: 'stb-underline', styles: { textDecoration: 'underline' }, exact: true, links: true },
								strikethrough: { inline: 'span', classes: 'stb-line-through', styles: { textDecoration: 'line-through' }, exact: true, links: true },
								forecolor: { inline: 'span', classes: 'stb-fore-colored', styles: { color: '%value' }, links: true, remove_similar: true, clear_child_styles: true },
								hilitecolor: { inline: 'span', classes: 'stb-hilitecolor-colored', styles: { backgroundColor: '%value' }, links: true, remove_similar: true, clear_child_styles: true },
								removeformat: [
									{
										selector: 'div, span, p, b, i, em, strong, u, strike, s, font, code, blockquote, table, tr, td, small, sub, sup, pre, cite, h1, h2, h3, h4, h5, h6',
										remove: 'all',
										deep: true,
										block_expand: true,
									},
								],
							},
							contextmenu: false,
							anchor_bottom: false,
							anchor_top: false,
							valid_elements: "p[*],br[*],b[*],i[*],strong[*],em[*],i[*],h1[*],h2[*],h3[*],h4[*],h5[*],h6[*],a[*],ul[*],ol[*],li[*],span[*],div[style]",
							invalid_elements: "*[contenteditable],script,iframe,object,embed",
							invalid_styles: { '*': 'position top left bottom right opacity' },
							extended_valid_elements: "a[href|target|rel|title|class|style]",
							// paste_as_text: true,
							paste_preprocess: function (plugin, args) {
								let res = addLinkToText(cleanUpHtml(trimBlankString(args.content)));
								args.content = res;
							},
							paste_postprocess: function (plugin, args) {
								removeInvalidStyleAndAttr(args.node);
							},
							// paste_webkit_styles : 'color font-size font-weight font-style text-decoration background background-color text-align line-through', // 일반 웹페이지 스타일 적용
							paste_retain_style_properties: 'color font-size font-weight font-style text-decoration background background-color text-align line-through', // MS word, google doc 스타일 적용
						}}
					/>
					{
						foreColorPickerShow && (
							<div className="color-picker-in-text fore" >
								<ColorPickerPanel
									value={foreColorValue}
									position="left"
									onChangeValue={onChangeForeColorValue}
									onResetValue={() => { onChangeForeColorValue('') }}
									onClose={() => { toggleForeColorPickerShow(false) }}
									className={colorPanelFixed ? 'fix' : ''}
									leftPosition={colorPanelFixedLeft}
								/>
							</div>
						)
					}
					{
						backColorPickerShow && (
							<div className="color-picker-in-text back" >
								<ColorPickerPanel
									value={backColorValue}
									position="left"
									onChangeValue={onChangeBackColorValue}
									onResetValue={() => { onChangeBackColorValue('') }}
									onClose={() => { toggleBackColorPickerShow(false) }}
									className={colorPanelFixed ? 'fix' : ''}
									leftPosition={colorPanelFixedLeft}
								/>
							</div>
						)
					}
					{
						emojiShow && (
							<div className="color-picker-in-text emoji">
								<EmojiPickerPanel
									className={colorPanelFixed ? 'fix' : ''}
									onEmojiClick={onEmojiClick}
									onClose={() => { toggleEmojiPicker(false) }}
								/>
							</div>
						)
					}
					{
																	aiModalShow && (
			<AIImprovementPanel
				onClose={() => toggleAiModal(false)}
				selectedText={selectedTextForAi}
				improvedText={improvedText}
				onAppendBelow={handleAppendBelow}
				onReplace={handleReplace}
				onRetry={handleRetry}
				onCancel={handleCancel}
				isLoading={isAiLoading}
				requestCount={aiRequestCount}
				maxRequestCount={aiMaxRequestCount}
				error={aiError}
			/>
			)
					}
				</div>
			)}
			{showContent && (
				<TextEditCover
					contentId={contentId}
					contentIndex={contentIndex}
					startEditing={startEditing}
					style={style}
					editedText={editedText}
					contentCategory={contentCategory || 'base'}
					commonStyle={commonStyle}
				/>
			)}
		</div>
	);
}

TextEdit.defaultProps = {
	contentField: 'text',
	contentCategory: 'base',
};

export default TextEdit;